import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  IconButton,
  Drawer,
  Stack,
  Skeleton,
  Pagination,
  useTheme,
  useMediaQuery,
  SelectChangeEvent,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
  GridView,
  ViewList,
  Map as MapIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import SearchBar from '../components/search/SearchBar';
import PropertyCard, { PropertyType } from '../components/property/PropertyCard';
import { mockProperties } from '../utils/mockData';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

type ViewMode = 'grid' | 'list' | 'map';

const ListingsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState<PropertyType[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<PropertyType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  
  // Filter states
  const [priceRange, setPriceRange] = useState<number[]>([0, 5000000]);
  const [bedrooms, setBedrooms] = useState<string>('any');
  const [bathrooms, setBathrooms] = useState<string>('any');
  const [propertyTypes, setPropertyTypes] = useState<{
    house: boolean;
    apartment: boolean;
    condo: boolean;
    townhouse: boolean;
  }>({
    house: false,
    apartment: false,
    condo: false,
    townhouse: false,
  });

  const propertyTypesArray = Object.entries(propertyTypes)
    .filter(([_, isSelected]) => isSelected)
    .map(([type]) => type);

  const propertiesPerPage = viewMode === 'list' ? 10 : 12;

  useEffect(() => {
    const fetchProperties = async () => {
      setIsLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setProperties(mockProperties);
        
        const queryParams = Object.fromEntries(searchParams.entries());
        
        if (queryParams.minPrice && queryParams.maxPrice) {
          setPriceRange([
            parseInt(queryParams.minPrice), 
            parseInt(queryParams.maxPrice)
          ]);
        }
        
        if (queryParams.bedrooms) {
          setBedrooms(queryParams.bedrooms);
        }
        
        if (queryParams.bathrooms) {
          setBathrooms(queryParams.bathrooms);
        }
        
        if (queryParams.propertyType) {
          const types = queryParams.propertyType.split(',');
          setPropertyTypes({
            house: types.includes('house'),
            apartment: types.includes('apartment'),
            condo: types.includes('condo'),
            townhouse: types.includes('townhouse'),
          });
        }
        
        if (queryParams.sort) {
          setSortBy(queryParams.sort);
        }
        
        if (queryParams.view) {
          setViewMode(queryParams.view as ViewMode);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperties();
  }, [searchParams]);

  useEffect(() => {
    if (properties.length === 0) return;
    
    let results = [...properties];
    
    results = results.filter(
      (property) => property.price >= priceRange[0] && property.price <= priceRange[1]
    );
    
    if (bedrooms !== 'any') {
      const minBedrooms = parseInt(bedrooms);
      results = results.filter((property) => property.bedrooms >= minBedrooms);
    }
    
    if (bathrooms !== 'any') {
      const minBathrooms = parseFloat(bathrooms);
      results = results.filter((property) => property.bathrooms >= minBathrooms);
    }
    
    if (propertyTypesArray.length > 0) {
      results = results.filter((property) => 
        propertyTypesArray.includes(property.type)
      );
    }
    
    switch (sortBy) {
      case 'price-asc':
        results.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        results.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        results.sort((a, b) => (a.isNew === b.isNew) ? 0 : a.isNew ? -1 : 1);
        break;
      default:
        break;
    }
    
    setFilteredProperties(results);
    setPage(1);
  }, [properties, priceRange, bedrooms, bathrooms, propertyTypesArray, sortBy]);

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setSearchParams(prev => {
        prev.set('view', newMode);
        return prev;
      });
    }
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as string);
  };

  const handlePropertyTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPropertyTypes({
      ...propertyTypes,
      [event.target.name]: event.target.checked,
    });
  };

  const handlePriceChange = (event: Event, newValue: number | number[]) => {
    setPriceRange(newValue as number[]);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleBedroomsChange = (event: SelectChangeEvent) => {
    setBedrooms(event.target.value);
  };

  const handleBathroomsChange = (event: SelectChangeEvent) => {
    setBathrooms(event.target.value);
  };

  const handleClearFilters = () => {
    setPriceRange([0, 5000000]);
    setBedrooms('any');
    setBathrooms('any');
    setPropertyTypes({
      house: false,
      apartment: false,
      condo: false,
      townhouse: false,
    });
  };

  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
  };

  const formatPrice = (value: number) => {
    return `$${value.toLocaleString()}`;
  };

  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const paginatedProperties = filteredProperties.slice(
    (page - 1) * propertiesPerPage,
    page * propertiesPerPage
  );

  const filtersContent = (
    <Box sx={{ p: isMobile ? 3 : 0 }}>
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Filters
          </Typography>
          <IconButton onClick={toggleMobileFilters}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Price Range
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={priceRange}
            onChange={handlePriceChange}
            valueLabelDisplay="auto"
            min={0}
            max={5000000}
            step={50000}
            valueLabelFormat={formatPrice}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {formatPrice(priceRange[0])}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatPrice(priceRange[1])}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Property Type
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={propertyTypes.house}
                onChange={handlePropertyTypeChange}
                name="house"
              />
            }
            label="House"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={propertyTypes.apartment}
                onChange={handlePropertyTypeChange}
                name="apartment"
              />
            }
            label="Apartment"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={propertyTypes.condo}
                onChange={handlePropertyTypeChange}
                name="condo"
              />
            }
            label="Condo"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={propertyTypes.townhouse}
                onChange={handlePropertyTypeChange}
                name="townhouse"
              />
            }
            label="Townhouse"
          />
        </FormGroup>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Bedrooms
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={bedrooms}
            onChange={handleBedroomsChange}
            displayEmpty
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="1">1+</MenuItem>
            <MenuItem value="2">2+</MenuItem>
            <MenuItem value="3">3+</MenuItem>
            <MenuItem value="4">4+</MenuItem>
            <MenuItem value="5">5+</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Bathrooms
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={bathrooms}
            onChange={handleBathroomsChange}
            displayEmpty
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="1">1+</MenuItem>
            <MenuItem value="1.5">1.5+</MenuItem>
            <MenuItem value="2">2+</MenuItem>
            <MenuItem value="3">3+</MenuItem>
            <MenuItem value="4">4+</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Button
        variant="outlined"
        color="primary"
        fullWidth
        onClick={handleClearFilters}
        sx={{ mt: 2 }}
      >
        Clear All Filters
      </Button>
    </Box>
  );

  const renderProperties = () => {
    if (isLoading) {
      return (
        <Grid container spacing={3}>
          {Array.from(new Array(6)).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" width="70%" height={30} />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="60%" />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Skeleton variant="circular" width={30} height={30} />
                    <Skeleton variant="circular" width={30} height={30} />
                    <Skeleton variant="circular" width={30} height={30} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (paginatedProperties.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <TuneIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            No Properties Found
          </Typography>
          <Typography color="text.secondary" paragraph>
            Try adjusting your search filters to find more properties.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClearFilters}
          >
            Clear All Filters
          </Button>
        </Box>
      );
    }

    if (viewMode === 'map') {
      return (
        <Box sx={{ height: 'calc(100vh - 300px)', minHeight: 500 }}>
          <MapContainer
            center={[40.7128, -74.0060]} // New York coordinates
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {paginatedProperties.map((property) => (
              <Marker
                key={property.id}
                position={[
                  40.7128 + Math.random() * 0.02 - 0.01,
                  -74.0060 + Math.random() * 0.02 - 0.01,
                ]}
              >
                <Popup>
                  <Box sx={{ maxWidth: 200 }}>
                    <img
                      src={property.images[0]}
                      alt={property.title}
                      style={{ width: '100%', height: 120, objectFit: 'cover' }}
                    />
                    <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
                      {property.title}
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(property.price)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {property.bedrooms} beds • {property.bathrooms} baths • {property.area} sq ft
                    </Typography>
                  </Box>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
      );
    }

    return viewMode === 'grid' ? (
      <Grid container spacing={3}>
        {paginatedProperties.map((property) => (
          <Grid item xs={12} sm={6} md={4} key={property.id}>
            <PropertyCard property={property} />
          </Grid>
        ))}
      </Grid>
    ) : (
      <Stack spacing={2}>
        {paginatedProperties.map((property) => (
          <PropertyCard
            key={property.id}
            property={property}
            variant="horizontal"
          />
        ))}
      </Stack>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" fontWeight={700} gutterBottom textAlign="center">
            Find Your Perfect Property
          </Typography>
          <Typography 
            variant="h6" 
            textAlign="center" 
            sx={{ mb: 4, opacity: 0.9, maxWidth: 700, mx: 'auto' }}
          >
            Browse our comprehensive listings of properties for sale and rent
          </Typography>
          <SearchBar showFilters={false} />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Card sx={{ position: 'sticky', top: 90, mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Filter Results
                </Typography>
                {filtersContent}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={9}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h6" component="div">
                {isLoading ? (
                  <Skeleton width={150} />
                ) : (
                  `${filteredProperties.length} Properties Found`
                )}
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label="view mode"
                  size="small"
                >
                  <ToggleButton value="grid" aria-label="grid view">
                    <GridView />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="list view">
                    <ViewList />
                  </ToggleButton>
                  <ToggleButton value="map" aria-label="map view">
                    <MapIcon />
                  </ToggleButton>
                </ToggleButtonGroup>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="sort-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-label"
                    value={sortBy}
                    label="Sort By"
                    onChange={handleSortChange}
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="price-asc">Price: Low to High</MenuItem>
                    <MenuItem value="price-desc">Price: High to Low</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={toggleMobileFilters}
                  >
                    Filters
                  </Button>
                </Box>
              </Stack>
            </Box>

            <Drawer
              anchor="right"
              open={mobileFiltersOpen}
              onClose={toggleMobileFilters}
              PaperProps={{
                sx: { width: '80%', maxWidth: 350, p: 2 },
              }}
            >
              {filtersContent}
            </Drawer>

            {renderProperties()}

            {!isLoading && totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ListingsPage;